import React, { useState } from "react";
import { createPortal } from "react-dom";
import {
  CheckOutlined,
  MinusOutlined,
  ExclamationOutlined,
  InfoCircleOutlined,
  CalendarOutlined,
  ApartmentOutlined,
  CheckCircleFilled,
  ArrowLeftOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  setOrgMode,
  clearSetupError,
} from "../../../../redux/OrganizationSetup/organizationSetupSlice";
import { updateUserOrgMode } from "../../../../redux/auth/authSlice";
import PopupCard from "../../../Auth/PopupCard";
import Location from "../LocationAndDepartment/Location/location";
import Department from "../LocationAndDepartment/Department/Deparment";

/* ─────────────── small helpers ─────────────── */
const RadioDot = ({ selected }) => (
  <span
    className={`inline-flex items-center justify-center w-[22px] h-[22px] rounded-full border-2 flex-shrink-0 transition-colors ${
      selected
        ? "border-[#06513D] bg-[#D0E3D6]"
        : "border-[#06513D] bg-[#D0E3D6]"
    }`}
  >
    {selected && <div className="w-2.5 h-2.5 rounded-full bg-[#06513D]" />}
  </span>
);

const MonthPill = ({ label, dots }) => {
  if (label) {
    return (
      <div className="flex-shrink-0">
        <span className="inline-block px-3 py-1.5 bg-white border border-[#06513D] rounded text-[13px] font-medium text-[#111827] text-center min-w-[50px]">
          {label}
        </span>
      </div>
    );
  }
  return (
    <div className="flex-1 px-2 flex items-center overflow-hidden opacity-80">
      <div className="w-full flex justify-between items-center">
        {[...Array(14)].map((_, i) => (
          <div key={i} className="w-[3px] h-[3px] rounded-full bg-[#06513D]" />
        ))}
      </div>
    </div>
  );
};

const InfoBox = ({ children }) => (
  <div className="flex-shrink-0 w-full lg:w-[220px] bg-[#EEF6FF] rounded-xl p-4 text-[#374151] text-[13px] leading-[1.6]">
    <InfoCircleOutlined className="text-[#3B82F6] text-base mb-2 block" />
    {children}
  </div>
);

/* ── locked summary chip ── */
const SummaryChip = ({ label, value }) => (
  <div className="flex items-center gap-2 bg-[#F0FAF6] border border-[#06513D]/20 rounded-lg px-4 py-2">
    <CheckCircleFilled className="text-[#06513D] text-base" />
    <span className="text-[13px] text-[#374151]">
      <span className="font-semibold text-[#06513D]">{label}:</span> {value}
    </span>
  </div>
);

/* ─────────────────── main component ─────────────────── */
const OrganizationSetup = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { loading: apiLoading, error: apiError } = useSelector(
    (state) => state.orgSetup,
  );

  const user = useSelector((state) => state.auth.user);
  const orgMode = user?.orgStructureMode;
  const orgModeLocked = user?.orgModeLocked;

  /* Setup wizard state */
  const [selectedReportingYear, setSelectedReportingYear] = useState(null);
  const [selectedStructure, setSelectedStructure] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showReportingLockedPopup, setShowReportingLockedPopup] =
    useState(false);
  const [lockedPendingChoice, setLockedPendingChoice] = useState(null);

  React.useEffect(() => {
    if (apiError) {
      const timer = setTimeout(() => dispatch(clearSetupError()), 2000);
      return () => clearTimeout(timer);
    }
  }, [apiError, dispatch]);

  const handleConfirm = async () => {
    const payload = {
      orgStructureMode: selectedStructure,
      reportingYearType: selectedReportingYear,
    };

    const result = await dispatch(setOrgMode(payload));
    if (setOrgMode.fulfilled.match(result)) {
      dispatch(updateUserOrgMode(payload));
      setIsModalOpen(false);
    } else if (setOrgMode.rejected.match(result)) {
      const errorMessage = result.payload || "";
      if (
        errorMessage.includes("already locked") ||
        errorMessage.includes("cannot be changed")
      ) {
        dispatch(updateUserOrgMode(payload));
        setIsModalOpen(false);
        dispatch(clearSetupError());
      }
    }
  };

  /* ── reporting year data ── */
  const reportingYearOptions = [
    {
      id: "financial",
      title: "Financial Year (Apr-Mar)",
      subtitle: "April to March",
      example: "Example: FY 2026-27",
      months: ["Apr", null, "Sep", null, "Mar"],
    },
    {
      id: "calendar",
      title: "Calendar Year (Jan-Dec)",
      subtitle: "January to December",
      example: "Example: 2026",
      months: ["Jan", null, "Jul", null, "Dec"],
    },
  ];

  /* ── org structure data ── */
  const structures = [
    {
      id: "location-first",
      title: "Location → Department",
      subtitle: "Departments under each location",
      mainNode: "Warehouse Mumbai",
      children: ["Sales", "Transport", "Marketing", "IT", "Admin"],
    },
    {
      id: "department-first",
      title: "Department → Location",
      subtitle: "Locations under each department",
      mainNode: "Sales",
      children: [
        "Warehouse Mumbai",
        "Warehouse Delhi",
        "Corporate Office Delhi",
        "Corporate Office Mumbai",
        "Headquarters",
      ],
    },
  ];

  /* ── helper: human readable labels ── */
  const reportingYearLabel =
    selectedReportingYear === "financial"
      ? "Financial Year (Apr-Mar)"
      : "Calendar Year (Jan-Dec)";

  const structureLabel =
    orgMode === "department-first"
      ? "Department → Location"
      : "Location → Department";

  /* ══════════════════════════════════════════════════
     LOCKED VIEW — setup done, show summary + tables
  ══════════════════════════════════════════════════ */
  if (orgModeLocked) {
    const showLocationFirst = orgMode !== "department-first";

    // Determine locked values for display
    const lockedReportingYear = user?.reportingYearType || "financial";
    const lockedStructure = orgMode || "location-first";

    const lockedReportingOptions = [
      {
        id: "financial",
        title: "Financial Year (Apr – Mar)",
        subtitle: "April to March",
        example: "Example: FY 2026-27",
        months: ["Apr", null, "Sep", null, "Mar"],
      },
      {
        id: "calendar",
        title: "Calendar Year (Jan – Dec)",
        subtitle: "January to December",
        example: "Example: 2026",
        months: ["Jan", null, "Jul", null, "Dec"],
      },
    ];

    const lockedStructures = [
      {
        id: "location-first",
        title: "Location → Department",
        subtitle: "Departments under each location",
        mainNode: "Warehouse Mumbai",
        children: ["Sales", "Transport", "Marketing", "IT", "Admin"],
      },
      {
        id: "department-first",
        title: "Department → Location",
        subtitle: "Locations under each department",
        mainNode: "Sales",
        children: [
          "Warehouse Mumbai",
          "Warehouse Delhi",
          "Corporate Office Delhi",
          "Corporate Office Mumbai",
          "Headquarters",
        ],
      },
    ];

    return (
      <div className="min-h-screen bg-[#F5F7F6] flex flex-col">
        {/* Header */}
        <div className="flex items-center gap-3 px-8 pt-6 pb-4">
          <div
            className="w-8 h-8 rounded-full border-2 border-[#06513D] flex items-center justify-center cursor-pointer hover:bg-[#F0FAF6]"
            onClick={() => navigate(-1)}
          >
            <ArrowLeftOutlined className="text-[#06513D]" />
          </div>
          <h1 className="text-[18px] font-bold text-[#06513D]">
            Add Locations & Departments
          </h1>
        </div>

        <div className="flex-1 px-8 pb-6 space-y-6 overflow-y-auto">
          {/* ════ Section 1: Reporting Configuration ════ */}
          <div
            className="bg-white rounded-[24px] border border-[#D0E3D6] p-8"
            style={{ boxShadow: "0px 0px 16px 0px #06513D1F" }}
          >
            <div className="mb-4">
              <h2 className="text-[18px] font-bold text-[#06513D] leading-tight">
                1. Reporting Configuration
              </h2>
            </div>
            <div className="flex flex-col lg:flex-row gap-3">
              <div className="flex flex-col sm:flex-row gap-3 flex-1">
                {reportingYearOptions.map((opt) => {
                  const sel = lockedReportingYear === opt.id;
                  return (
                    <div
                      key={opt.id}
                      onClick={() => {
                        // If org mode is locked and user has a reportingYearType, show a popup when clicking a different option
                        if (
                          user?.reportingYearType &&
                          user.reportingYearType !== opt.id
                        ) {
                          setLockedPendingChoice(opt.id);
                          setShowReportingLockedPopup(true);
                          return;
                        }
                      }}
                      className={`flex-1 rounded-[16px] border px-5 py-4 transition-all ${
                        sel
                          ? "border-[#06513D] bg-white"
                          : "border-[#D1D5DB] bg-white"
                      }`}
                      style={{ boxShadow: "0px 0px 16px 0px #06513D1F" }}
                    >
                      <div className="flex items-center gap-4 mb-2">
                        <RadioDot selected={sel} />
                        <div>
                          <p className="text-[18px] font-medium text-[#000000] mb-[2px]">
                            {opt.title}
                          </p>
                          <p className="text-[12px] text-[#000000]">
                            {opt.subtitle}
                          </p>
                        </div>
                      </div>
                      <hr className="border-t border-[#06513D]/20 mb-4 mt-3 -mx-5" />
                      <div className="flex items-center gap-3 mb-1 mt-2">
                        <p className="text-[14px] text-[#111827] font-medium">
                          Example:
                        </p>
                        <p className="text-[14px] text-[#111827]">
                          {opt.example.replace("Example: ", "")}
                        </p>
                      </div>
                      <div className="flex items-center justify-between w-full mb-4">
                        {opt.months.map((m, i) =>
                          m ? (
                            <MonthPill key={i} label={m} />
                          ) : (
                            <MonthPill key={i} dots />
                          ),
                        )}
                      </div>
                    </div>
                  );
                })}
                {showReportingLockedPopup &&
                  createPortal(
                    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 px-4">
                      <PopupCard
                        type="warning"
                        title="Year Type Already Set"
                        message={
                          "The reporting year type has already been configured. You cannot change the year type once it has been selected."
                        }
                        onConfirm={() => {
                          setShowReportingLockedPopup(false);
                          setLockedPendingChoice(null);
                        }}
                        confirmText="OK"
                      />
                    </div>,
                    document.body,
                  )}
              </div>
            </div>
          </div>

          {/* ════ Section 2: Organization Structure ════ */}
          <div
            className="bg-white rounded-[24px] border border-[#D0E3D6] p-8"
            style={{ boxShadow: "0px 0px 16px 0px #06513D1F" }}
          >
            <div className="mb-4">
              <h2 className="text-[18px] font-bold text-[#06513D] leading-tight">
                2. Organization Structure
              </h2>
              <div className="mt-2 inline-flex items-center px-3 py-1 bg-[#F0FAF6] border border-[#06513D] rounded-md text-[13px]">
                <span className="font-bold mr-1 text-[#06513D]">
                  You Selected:
                </span>{" "}
                <span className="text-[#000000] font-medium">
                  {lockedStructure === "location-first"
                    ? "Location → Department"
                    : "Department → Location"}
                </span>
              </div>
            </div>

            {/* ── Location & Department tables ── */}
            <div className="space-y-8 mt-6">
              {showLocationFirst ? (
                <>
                  <Location
                    onOpenImportLocation={() => navigate("import-location")}
                  />
                  <Department
                    onOpenImportDepartment={() => navigate("import-department")}
                  />
                </>
              ) : (
                <>
                  <Department
                    onOpenImportDepartment={() => navigate("import-department")}
                  />
                  <Location
                    onOpenImportLocation={() => navigate("import-location")}
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* ══════════════════════════════════════════════════
     SETUP WIZARD — org not yet configured
  ══════════════════════════════════════════════════ */
  return (
    <div className="min-h-screen bg-[#F5F7F6] flex flex-col">
      {/* Page header */}
      <div className="px-6 pt-6 pb-2">
        <h1 className="text-[22px] font-bold text-[#06513D]">
          Organization Setup
        </h1>
        <p className="text-[13px] text-[#6B7280] mt-0.5">
          Configure your organization settings. These configurations will be
          applied across all projects.
        </p>
      </div>

      {/* Scrollable sections */}
      <div className="flex-1 px-8 pb-6 pt-2 overflow-y-auto">
        <div
          className="bg-white rounded-[24px] border border-[#D0E3D6] p-8 space-y-10"
          style={{ boxShadow: "0px 0px 16px 0px #06513D1F" }}
        >
          {/* ════ Section 1: Reporting Configuration ════ */}
          <div>
            <div className="mb-4">
              <h2 className="text-[18px] font-bold text-[#06513D] leading-tight">
                1. Reporting Configuration
              </h2>
              <p className="text-[14px] text-[#111827] mt-1 leading-tight">
                Choose the reporting calendar that your organization follows.
              </p>
            </div>

            <div>
              <div className="flex flex-col lg:flex-row gap-3">
                <div className="flex flex-col sm:flex-row gap-3 flex-1">
                  {reportingYearOptions.map((opt) => {
                    const sel = selectedReportingYear === opt.id;
                    return (
                      <div
                        key={opt.id}
                        onClick={() => setSelectedReportingYear(opt.id)}
                        className={`flex-1 rounded-[16px] border px-5 py-4 cursor-pointer transition-all ${
                          sel
                            ? "border-[#06513D] bg-white"
                            : "border-[#D1D5DB] bg-white hover:border-[#06513D]/40"
                        }`}
                        style={{ boxShadow: "0px 0px 16px 0px #06513D1F" }}
                      >
                        <div className="flex items-center gap-4 mb-2">
                          <RadioDot selected={sel} />
                          <div>
                            <p className="text-[18px] font-medium text-[#000000] mb-[2px]">
                              {opt.title}
                            </p>
                            <p className="text-[12px] text-[#000000]">
                              {opt.subtitle}
                            </p>
                          </div>
                        </div>
                        <hr className="border-t border-[#06513D]/20 mb-4 mt-3 -mx-5" />
                        <div className="flex items-center gap-3 mb-1 mt-2">
                          <p className="text-[14px] text-[#111827] font-medium">
                            Example:
                          </p>
                          <p className="text-[14px] text-[#111827]">
                            {opt.example.replace("Example: ", "")}
                          </p>
                        </div>
                        <div className="flex items-center justify-between w-full mb-4">
                          {opt.months.map((m, i) =>
                            m ? (
                              <MonthPill key={i} label={m} />
                            ) : (
                              <MonthPill key={i} dots />
                            ),
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
          {/* ════ Section 2: Organization Structure ════ */}
          <div>
            <div className="mb-4 mt-6">
              <h2 className="text-[18px] font-bold text-[#06513D] leading-tight">
                2. Organization Structure
              </h2>
              <p className="text-[14px] text-[#111827] mt-1 leading-tight">
                Define the hierarchy of your organization.
              </p>
            </div>

            <div>
              <div className="flex flex-col lg:flex-row gap-4">
                <div className="flex flex-col sm:flex-row gap-4 flex-1">
                  {structures.map((structure, idx) => {
                    const sel = selectedStructure === structure.id;
                    return (
                      <div
                        key={structure.id}
                        onClick={() => setSelectedStructure(structure.id)}
                        className={`flex-1 rounded-[16px] border px-5 py-4 cursor-pointer transition-all ${
                          sel
                            ? "border-[#06513D] bg-white"
                            : "border-[#D1D5DB] bg-white hover:border-[#06513D]/40"
                        }`}
                        style={{ boxShadow: "0px 0px 16px 0px #06513D1F" }}
                      >
                        <div className="mb-2">
                          <div className="flex items-center gap-3">
                            <RadioDot selected={sel} />
                            <p className="text-[14px] text-[#000000] font-medium tracking-wider leading-none">
                              Option {idx + 1}
                            </p>
                          </div>
                          <div className="pl-[34px] -mt-1">
                            <p className="text-[18px] font-medium text-[#111827] leading-tight">
                              {structure.title}
                            </p>
                          </div>
                        </div>
                        <hr className="border-t border-[#06513D]/20 mb-4 mt-3 -mx-5" />

                        <p className="text-[14px] text-[#111827] font-medium mb-4">
                          Example:
                        </p>

                        <div className="relative pl-2">
                          <div className="absolute left-[17px] top-[22px] bottom-2 w-[2px] bg-[#06513D]/40 z-0" />
                          <div className="flex items-center gap-3 mb-4 relative z-10">
                            <div className="w-5 h-5 rounded-full bg-[#D0E3D6] border-2 border-[#06513D] flex-shrink-0" />
                            <span className="text-[14px] font-bold text-[#06513D]">
                              {structure.mainNode}
                            </span>
                          </div>
                          <div className="flex flex-col gap-3">
                            {structure.children.map((child, idx) => (
                              <div
                                key={idx}
                                className="flex items-center relative z-10 pl-9 mb-3 last:mb-0"
                              >
                                <div className="absolute left-[10px] top-1/2 w-[26px] h-[2px] bg-[#06513D]/40 -translate-y-1/2 z-0" />
                                <div className="w-3.5 h-3.5 rounded-full bg-[#D0E3D6] border-[1.5px] border-[#06513D] flex-shrink-0 relative z-10" />
                                <span className="ml-3 text-[13px] text-[#111827] font-medium relative z-10">
                                  {child}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
          <div className=" bg-white flex items-center justify-end ">
            <button
              onClick={() => setIsModalOpen(true)}
              disabled={!selectedReportingYear}
              className={`px-6 h-[38px] rounded-lg text-white text-[13px] font-semibold transition-colors border-none ${
                !selectedReportingYear
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-[#06513D] hover:bg-[#054131] cursor-pointer"
              }`}
            >
              Save &amp; Continue
            </button>
          </div>
        </div>{" "}
        {/* End of main card container */}
      </div>

      {/* Confirmation Modal */}
      {isModalOpen &&
        createPortal(
          <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 px-4">
            <div className="bg-white rounded-2xl border border-[#42b8a3] p-8 w-full max-w-[560px] shadow-2xl">
              <div className="flex flex-col items-center text-center">
                <div className="w-16 h-16 rounded-full bg-[#D0E3D6] border-2 border-[#06513D] flex items-center justify-center mb-5">
                  <ExclamationOutlined className="text-[#06513D] text-3xl font-bold" />
                </div>
                <h2 className="text-[#06513D] text-[20px] font-bold mb-3">
                  Confirm Organization Setup
                </h2>
                <p className="text-[#374151] text-[14px] leading-relaxed mb-5 max-w-[460px]">
                  You’re about to finalize your organization setup. Once
                  confirmed, the Reporting Year Type & Organization Hierarchy
                  cannot be changed later.
                </p>
                <p className="text-[#111827] text-[16px] font-medium mb-7">
                  Please review your selections carefully before continuing.
                </p>

                {apiError &&
                  createPortal(
                    <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/40 px-4">
                      <PopupCard
                        type="error"
                        title="Error"
                        message={apiError}
                      />
                    </div>,
                    document.body,
                  )}

                <div className="flex flex-col sm:flex-row gap-3 w-full justify-center">
                  <button
                    onClick={() => setIsModalOpen(false)}
                    disabled={apiLoading}
                    className="px-10 h-[40px] rounded-lg border border-[#42b8a3] bg-[#E9F4F1] text-[#06513D] font-semibold text-[14px] hover:bg-[#D0E3D6] transition-colors disabled:opacity-50 cursor-pointer min-w-[130px]"
                  >
                    Go Back & Review
                  </button>

                  <button
                    onClick={handleConfirm}
                    disabled={apiLoading}
                    className="px-10 h-[40px] rounded-lg bg-[#06513D] text-white font-semibold text-[14px] hover:bg-[#054131] transition-colors disabled:opacity-60 cursor-pointer border-none flex items-center justify-center gap-2 min-w-[130px]"
                  >
                    {apiLoading ? (
                      <>
                        <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Saving...
                      </>
                    ) : (
                      "Confirm & Continue"
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default OrganizationSetup;
